import { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import { PlusIcon } from '@phosphor-icons/react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  DimensionType,
  TableConfig,
  MultiYearForecastData,
  DimensionGroupingConfig,
} from '@/types/rebateAnalytics';
import type { TimeFilterPreset as TimeFilterPresetType } from '@/types/rebateAnalytics';
import { generateTableName } from '@/utils/rebateAnalyticsTableUtils';
import TableConfigFormFields from './RebateAnalyticsTableConfigForm';
import { RebateAnalyticsTablePreview } from './RebateAnalyticsTablePreview';

interface CreateAnalyticsTableModalProps {
  open: boolean;
  onClose: () => void;
  onSave: (config: Omit<TableConfig, 'id' | 'label'>, label: string) => void;
  creatorName?: string;
  multiYearForecastData: MultiYearForecastData;
  supplierIdToName: Map<string, string>;
  divisionIdToName: Map<string, string>;
  isCumulative: boolean;
  ruleTagIdToName?: Map<string, string>;
  agreementTagIdToName?: Map<string, string>;
  supplierTagIdToName?: Map<string, string>;
  ruleTagsMap?: Record<string, string[]>;
  agreementTagsMap?: Record<string, string[]>;
  supplierTagsMap?: Record<string, string[]>;
  ruleIdToName?: Map<string, string>;
  agreementIdToName?: Map<string, string>;
}

export function CreateAnalyticsTableModal({
  open,
  onClose,
  onSave,
  creatorName,
  multiYearForecastData,
  supplierIdToName,
  divisionIdToName,
  isCumulative,
  ruleTagIdToName,
  agreementTagIdToName,
  supplierTagIdToName,
  ruleTagsMap,
  agreementTagsMap,
  supplierTagsMap,
  ruleIdToName,
  agreementIdToName,
}: CreateAnalyticsTableModalProps) {
  const forecastData = multiYearForecastData.currentYearData;

  // New 4-dimension structure
  const [primaryXDimension] = useState<DimensionType>(DimensionType.TIME); // Always TIME, locked
  const [secondaryXDimension, setSecondaryXDimension] = useState<
    DimensionType | undefined
  >(undefined);
  const [primaryYDimension, setPrimaryYDimension] = useState<
    DimensionType | undefined
  >(DimensionType.DIVISION); // Default to Division
  const [secondaryYDimension, setSecondaryYDimension] = useState<
    DimensionType | undefined
  >(undefined);

  // Dimension grouping config state (supports all dimensions)
  const [dimensionGroupingConfig, setDimensionGroupingConfig] = useState<
    Partial<
      Record<
        Exclude<DimensionType, DimensionType.TIME>,
        DimensionGroupingConfig
      >
    >
  >({});

  // Auto-generate name based on dimensions
  const autoGeneratedName = useMemo(
    () =>
      generateTableName(
        primaryXDimension,
        secondaryXDimension,
        primaryYDimension,
        secondaryYDimension,
        dimensionGroupingConfig,
      ),
    [
      primaryXDimension,
      secondaryXDimension,
      primaryYDimension,
      secondaryYDimension,
      dimensionGroupingConfig,
    ],
  );

  // Custom name state - tracks user-edited name
  const [customName, setCustomName] = useState<string | undefined>(undefined);
  const [hasDimensionChanged, setHasDimensionChanged] = useState(false);
  const prevAutoGeneratedNameRef = useRef<string>(autoGeneratedName);

  // Derived: check if name is custom
  const isCustomName = useMemo(() => {
    return customName !== undefined && customName !== autoGeneratedName;
  }, [customName, autoGeneratedName]);

  // Sync custom name when auto-generated name changes
  useEffect(() => {
    const prevAutoGen = prevAutoGeneratedNameRef.current;
    const autoGenChanged = prevAutoGen !== autoGeneratedName;

    if (autoGenChanged) {
      // If name was auto-gen (matches previous auto-gen), auto-update
      if (customName === undefined || customName === prevAutoGen) {
        setCustomName(undefined); // Reset to use auto-generated
        setHasDimensionChanged(false); // Reset flag since we auto-updated
      } else {
        // Name is custom, mark that dimensions changed
        setHasDimensionChanged(true);
      }
    }

    prevAutoGeneratedNameRef.current = autoGeneratedName;
  }, [autoGeneratedName, customName]);

  // Check for duplicate dimensions
  const hasDuplicates = useMemo(() => {
    const selected = [
      primaryXDimension,
      secondaryXDimension,
      primaryYDimension,
      secondaryYDimension,
    ].filter((d): d is DimensionType => d !== undefined);

    const seen = new Set<DimensionType>();
    for (const dim of selected) {
      if (seen.has(dim)) {
        return true;
      }
      seen.add(dim);
    }
    return false;
  }, [
    primaryXDimension,
    secondaryXDimension,
    primaryYDimension,
    secondaryYDimension,
  ]);

  const handleDimensionGroupingConfigChange = useCallback(
    (
      dimensionType: Exclude<DimensionType, DimensionType.TIME>,
      config: DimensionGroupingConfig | undefined,
    ) => {
      setDimensionGroupingConfig((prev) => {
        const next = { ...prev };
        if (config === undefined) {
          // Remove the dimension from config (no grouping)
          delete next[dimensionType];
        } else {
          // Set the grouping config
          next[dimensionType] = config;
        }
        return next;
      });
    },
    [],
  );

  // Validate name
  const displayName = customName ?? autoGeneratedName;
  const isValidName = useMemo(() => {
    const trimmed = displayName.trim();
    return trimmed !== '' && trimmed === displayName; // No leading/trailing whitespace
  }, [displayName]);

  const handleCreate = () => {
    if (hasDuplicates || !primaryYDimension || !isValidName) {
      return; // Don't create if duplicates exist, primaryYDimension is not set, or name is invalid
    }

    const config = {
      primaryXDimension,
      secondaryXDimension,
      primaryYDimension,
      secondaryYDimension,
      timeFilterPreset: 'all' as TimeFilterPresetType, // Default to 'all' for new tables
      dimensionGroupingConfig:
        Object.keys(dimensionGroupingConfig).length > 0
          ? dimensionGroupingConfig
          : undefined,
    };
    // Use custom name if provided and valid, otherwise use auto-generated
    const finalName = customName?.trim() || autoGeneratedName;
    onSave(config, finalName);

    // Reset all axes except TIME back to defaults
    setSecondaryXDimension(undefined);
    setPrimaryYDimension(DimensionType.DIVISION);
    setSecondaryYDimension(undefined);
    setDimensionGroupingConfig({});
    setCustomName(undefined);
    setHasDimensionChanged(false);

    // Close the modal
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-[95vw] w-[95vw] h-[90vh] max-h-[96vh] flex flex-col z-[1400] p-0 md:max-w-2xl lg:max-w-3xl xl:max-w-6xl">
        <DialogHeader className="px-6 pt-6 pb-4 border-b shrink-0">
          <DialogTitle>Create New Analytics Table</DialogTitle>
        </DialogHeader>
        <div className="flex-1 overflow-hidden px-6 pb-6 flex flex-col">
          <div className="pt-4 shrink-0">
            <TableConfigFormFields
              primaryXDimension={primaryXDimension}
              secondaryXDimension={secondaryXDimension}
              primaryYDimension={primaryYDimension}
              secondaryYDimension={secondaryYDimension}
              onSecondaryXDimensionChange={setSecondaryXDimension}
              onPrimaryYDimensionChange={(value) => {
                setPrimaryYDimension(value);
                // Reset secondary Y dimension when primary is set to None
                if (!value) {
                  setSecondaryYDimension(undefined);
                }
              }}
              onSecondaryYDimensionChange={setSecondaryYDimension}
              autoGeneratedName={autoGeneratedName}
              customName={customName}
              onCustomNameChange={setCustomName}
              hasDimensionChanged={hasDimensionChanged}
              isValidName={isValidName}
              hasDuplicates={hasDuplicates}
              showPreview={false}
              forecastData={forecastData}
              multiYearForecastData={multiYearForecastData}
              supplierIdToName={supplierIdToName}
              divisionIdToName={divisionIdToName}
              ruleTagIdToName={ruleTagIdToName}
              agreementTagIdToName={agreementTagIdToName}
              supplierTagIdToName={supplierTagIdToName}
              ruleTagsMap={ruleTagsMap}
              agreementTagsMap={agreementTagsMap}
              supplierTagsMap={supplierTagsMap}
              buttonLabel="Create"
              buttonIcon={<PlusIcon className="h-4 w-4" />}
              onSave={handleCreate}
              ruleIdToName={ruleIdToName}
              agreementIdToName={agreementIdToName}
              dimensionGroupingConfig={dimensionGroupingConfig}
              onDimensionGroupingConfigChange={
                handleDimensionGroupingConfigChange
              }
            />
          </div>
          <div className="flex-1 min-h-0 overflow-auto mt-4 pt-4 border-t">
            <RebateAnalyticsTablePreview
              multiYearForecastData={multiYearForecastData}
              primaryXDimension={primaryXDimension}
              secondaryXDimension={secondaryXDimension}
              primaryYDimension={primaryYDimension}
              secondaryYDimension={secondaryYDimension}
              useCumulative={isCumulative}
              supplierIdToName={supplierIdToName}
              divisionIdToName={divisionIdToName}
              ruleTagIdToName={ruleTagIdToName}
              agreementTagIdToName={agreementTagIdToName}
              supplierTagIdToName={supplierTagIdToName}
              ruleTagsMap={ruleTagsMap}
              agreementTagsMap={agreementTagsMap}
              supplierTagsMap={supplierTagsMap}
              hasDuplicates={hasDuplicates}
              dimensionGroupingConfig={dimensionGroupingConfig}
            />
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
